import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';
import defaultGroupPic from '../assets/defaultGroupPic.jpg'
import defaultProfilePic from '../assets/default-avatar.jpg'
import { 
  ArrowLeft, 
  Trophy, 
  Users, 
  Clock, 
  Calendar,
  Zap,
  TrendingUp,
  Award,
  Target,
  Activity,
  MapPin,
  Crown,
  Flame,
  CheckCircle,
  XCircle,
  Timer,
  Swords
} from 'lucide-react';
import { DotLoader } from 'react-spinners';

const ClubWarPage = () => {
  const { warId } = useParams();
  const navigate = useNavigate();
  const [war, setWar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [currentUser, setCurrentUser] = useState(null);
  const [syncingStrava, setSyncingStrava] = useState(false);

  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    fetchWarDetails();
    fetchCurrentUser();
    const interval = setInterval(() => {
      if (war?.isActive) {
        fetchWarDetails();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [warId]);

  useEffect(() => {
    if (!war || war.statut !== 'active') return;

    const calculateTimeRemaining = () => {
      const now = new Date();
      const endDate = new Date(war.dateFin);
      const diff = endDate - now;

      if (diff <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds });
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [war?.dateFin, war?.statut]);

  const fetchWarDetails = async () => {
    try {
      const response = await api.get(`/club-wars/${warId}`);
      setWar(response.data.war);
    } catch (error) {
      console.error('Erreur lors du chargement de la guerre:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/users/profile');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'utilisateur:', error);
    }
  };

  const handleAccept = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir accepter ce défi ?')) return;
    
    try {
      await api.post(`/club-wars/${warId}/accept`);
      alert('✅ Défi accepté ! Que la bataille commence !');
      fetchWarDetails();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors de l\'acceptation');
    }
  };

  const handleReject = async () => {
    const reason = prompt('Raison du refus (optionnel):');
    if (reason === null) return;
    
    try {
      await api.post(`/club-wars/${warId}/reject`, { reason });
      alert('❌ Défi refusé');
      navigate(-1);
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors du refus');
    }
  };

  const isParticipating = () => {
    if (!currentUser || !war) return false;
    
    const inInit = war.performances?.clubInitiateur?.participants?.some(
      p => p.user._id === currentUser._id || p.user === currentUser._id
    );
    const inDefie = war.performances?.clubDefie?.participants?.some(
      p => p.user._id === currentUser._id || p.user === currentUser._id
    );
    
    return inInit || inDefie;
  };

  const isMemberOfParticipatingClub = () => {
    if (!currentUser || !war) return false;
    
    const isInInitiateur = war.clubInitiateur.membres?.some(
      m => (typeof m === 'object' ? m._id : m).toString() === currentUser._id.toString()
    );
    
    const isInDefie = war.clubDefie.membres?.some(
      m => (typeof m === 'object' ? m._id : m).toString() === currentUser._id.toString()
    );
    
    return isInInitiateur || isInDefie;
  };

  const handleJoinWar = async () => {
    try {
      await api.post(`/club-wars/${warId}/join`);
      alert('✅ Vous participez maintenant à cette guerre !');
      fetchWarDetails();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors de l\'inscription');
    }
  };

  const handleLeaveWar = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir quitter cette guerre ?')) return;
    
    try {
      await api.post(`/club-wars/${warId}/leave`);
      alert('Vous avez quitté cette guerre');
      fetchWarDetails();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur');
    }
  };

  const handleSyncStrava = async () => {
    setSyncingStrava(true);
    try {
      const response = await api.post(`/club-wars/${warId}/sync-strava`);
      alert(`✅ ${response.data.message}`);
      if (response.data.errors && response.data.errors.length > 0) {
        console.log('Activités non ajoutées:', response.data.errors);
      }
      fetchWarDetails();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors de la synchronisation');
    } finally {
      setSyncingStrava(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-slate-950 flex items-center justify-center">
          <div className="text-center">
            <div className="mb-4">
              <DotLoader color="#6366f1" size={60} />
            </div>
            <p className="text-gray-400 font-semibold">Chargement de la bataille...</p>
          </div>
        </div>
      </>
    );
  }

  if (!war) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-slate-950 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">⚔️</div>
            <p className="text-gray-400 font-semibold text-xl">Guerre introuvable</p>
            <button
              onClick={() => navigate(-1)}
              className="mt-6 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition"
            >
              ← Retour
            </button>
          </div>
        </div>
      </>
    );
  }

  const getStatusConfig = (statut) => {
    const configs = {
      pending: { 
        text: 'En attente', 
        color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20', 
        icon: Timer 
      },
      active: { 
        text: 'En cours', 
        color: 'bg-green-500/10 text-green-500 border-green-500/20', 
        icon: Flame 
      },
      completed: { 
        text: 'Terminé', 
        color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', 
        icon: CheckCircle 
      },
      rejected: { 
        text: 'Refusé', 
        color: 'bg-red-500/10 text-red-500 border-red-500/20', 
        icon: XCircle 
      }
    };
    return configs[statut] || configs.pending;
  };

  const getTypeDefiLabel = (type) => {
    const labels = {
      distance_totale: { text: 'Distance totale', icon: Target, unite: 'km' },
      nombre_courses: { text: 'Nombre d\'activités', icon: Activity, unite: 'courses' },
      distance_moyenne: { text: 'Distance moyenne', icon: TrendingUp, unite: 'km/pers' },
      participation: { text: 'Participation', icon: Users, unite: '%' },
      denivele_total: { text: 'Dénivelé total', icon: TrendingUp, unite: 'm' },
      vitesse_moyenne: { text: 'Allure moyenne', icon: Zap, unite: 'min/km' }
    };
    return labels[type] || labels.distance_totale;
  };

  const scoreInit = war.scoreActuel?.clubInitiateur || 0;
  const scoreDefie = war.scoreActuel?.clubDefie || 0;
  const total = scoreInit + scoreDefie;
  const percentInit = total > 0 ? (scoreInit / total * 100) : 50;
  const percentDefie = total > 0 ? (scoreDefie / total * 100) : 50;

  const typeInfo = getTypeDefiLabel(war.typeDefi);
  const statusConfig = getStatusConfig(war.statut);
  const StatusIcon = statusConfig.icon;
  const TypeIcon = typeInfo.icon;

  const daysRemaining = Math.ceil((new Date(war.dateFin) - new Date()) / (1000 * 60 * 60 * 24));
  const isLeaderOfDefiedClub = currentUser && war.clubDefie?.createur === currentUser._id;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-950 pb-20">
        {/* Hero Header */}
        <div className="relative bg-slate-950">
          <div className="absolute inset-0 " />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
            {/* Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="mb-6 inline-flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-800 text-gray-300 hover:text-white rounded-xl transition border border-slate-700"
            >
              <ArrowLeft size={18} />
              <span>Retour</span>
            </button>

            {/* Title & Status */}
<div className="mb-8">
  {/* Titre et Description - Toujours centrés */}
  <div className="text-center mb-6">
    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 tracking-tight relative inline-block">
      <span className="relative z-10">{war.nom}</span>
      {/* Effet de shine sur le titre */}
      
    </h1>
    <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto">
      {war.description}
    </p>
  </div>

  {/* Timer et Badges */}
  <div className="flex flex-col items-center gap-4">
    {/* Countdown Timer */}
    {war.statut === 'active' && (
  <div className="relative inline-flex items-center gap-4 px-6 py-4 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 rounded-2xl border-2 border-slate-700 shadow-2xl overflow-hidden group">
    {/* Effet de brillance animé sur le timer */}
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
    
    <div className="relative z-10 flex items-center gap-3 sm:gap-4">
      <Clock size={20} className="text-indigo-400 animate-pulse" />
      
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Jours */}
        <div className="text-center w-12 sm:w-16">
          <div className="text-2xl sm:text-3xl font-bold text-white tabular-nums bg-gradient-to-br from-white to-gray-300 bg-clip-text text-transparent">
            {Math.floor(timeRemaining.days)}
          </div>
          <div className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase tracking-wider">
            Jours
          </div>
        </div>
        
        <span className="text-2xl font-bold text-gray-600">:</span>
        
        {/* Heures */}
        <div className="text-center w-12 sm:w-14">
          <div className="text-2xl sm:text-3xl font-bold text-white tabular-nums bg-gradient-to-br from-white to-gray-300 bg-clip-text text-transparent">
            {String(timeRemaining.hours).padStart(2, '0')}
          </div>
          <div className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase tracking-wider">
            Heures
          </div>
        </div>
        
        <span className="text-2xl font-bold text-gray-600">:</span>
        
        {/* Minutes */}
        <div className="text-center w-12 sm:w-14">
          <div className="text-2xl sm:text-3xl font-bold text-white tabular-nums bg-gradient-to-br from-white to-gray-300 bg-clip-text text-transparent">
            {String(timeRemaining.minutes).padStart(2, '0')}
          </div>
          <div className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase tracking-wider">
            Min
          </div>
        </div>
        
        <span className="text-2xl font-bold text-gray-600">:</span>
        
        {/* Secondes */}
        <div className="text-center w-12 sm:w-14">
          <div className="text-2xl sm:text-3xl font-bold text-indigo-400 tabular-nums animate-pulse">
            {String(timeRemaining.seconds).padStart(2, '0')}
          </div>
          <div className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase tracking-wider">
            Sec
          </div>
        </div>
      </div>
    </div>
    
    {/* Effet de particules brillantes */}
    <div className="absolute top-1 right-2 w-1 h-1 bg-indigo-400 rounded-full animate-ping opacity-75"></div>
    <div className="absolute bottom-1 left-2 w-1 h-1 bg-purple-400 rounded-full animate-ping opacity-75" style={{ animationDelay: '0.5s' }}></div>
  </div>
)}

    

    {/* Action Buttons for Pending Status */}
    {war.statut === 'pending' && isLeaderOfDefiedClub && (
      <div className="flex gap-3 mt-4">
        <button
          onClick={handleReject}
          className="px-6 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 rounded-xl font-semibold transition"
        >
          <XCircle size={18} className="inline mr-2" />
          Refuser
        </button>
        <button
          onClick={handleAccept}
          className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition shadow-lg shadow-green-500/20"
        >
          <CheckCircle size={18} className="inline mr-2" />
          Accepter
        </button>
      </div>
    )}
  </div>
</div>

            {/* Clubs Battle Card */}
<div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-6 shadow-2xl">
  <div className="grid md:grid-cols-3 gap-4 sm:gap-6 items-center">
    {/* Club Initiateur */}
    <div className="text-center">
      <div className="relative inline-block mb-3 sm:mb-4">
        <div className="relative">
          <img
            src={war.clubInitiateur.imageClub || defaultGroupPic}
            alt={war.clubInitiateur.nom}
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-indigo-500 shadow-lg shadow-indigo-500/50 object-cover"
          />
          {/* Effet de brillance */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-white/20 to-transparent animate-pulse"></div>
        </div>
        
      </div>
      <h3 className="font-bold text-base sm:text-xl text-white mb-1 truncate px-2">{war.clubInitiateur.nom}</h3>
      <p className="text-gray-400 text-xs sm:text-sm flex items-center justify-center gap-1">
        <MapPin size={12} className="sm:w-3.5 sm:h-3.5 flex-shrink-0" />
        <span className="truncate">{war.clubInitiateur.ville}</span>
      </p>
    </div>

    {/* VS Section */}
    <div className="text-center">
      <div className="relative inline-block mb-3 sm:mb-4">
        <div className="text-4xl sm:text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-purple-500 to-red-500 animate-pulse">
          VS
        </div>
        
      </div>
      
      {war.statut === 'active' && (
        <>
          {/* Score Display */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 mb-3 sm:mb-4">
            <div className="flex flex-col items-center">
              <div className="text-2xl sm:text-3xl font-bold text-indigo-400 tabular-nums">
                {scoreInit.toFixed(1)}
              </div>
              <div className="text-[10px] sm:text-xs text-gray-500 font-medium">km</div>
            </div>
            <div className="text-gray-600 font-bold">-</div>
            <div className="flex flex-col items-center">
              <div className="text-2xl sm:text-3xl font-bold text-red-400 tabular-nums">
                {scoreDefie.toFixed(1)}
              </div>
              <div className="text-[10px] sm:text-xs text-gray-500 font-medium">km</div>
            </div>
          </div>

          {/* Progress Bar améliorée */}
          <div className="relative">
            <div className="relative h-4 sm:h-7 bg-slate-800 rounded-full overflow-hidden border-2 border-slate-700 shadow-inner">
              {/* Partie indigo (initiateur) */}
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-600 via-indigo-500 to-indigo-400 transition-all duration-1000 ease-out overflow-hidden"
                style={{ width: `${percentInit}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full animate-shimmer-left"></div>
              </div>

              {/* Partie rouge (défié) */}
              <div
                className="absolute inset-y-0 right-0 bg-gradient-to-l from-red-600 via-red-500 to-red-400 transition-all duration-1000 ease-out overflow-hidden"
                style={{ width: `${percentDefie}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-l from-transparent via-white/30 to-transparent translate-x-full animate-shimmer-right"></div>
              </div>

              {/* Ligne centrale */}
              <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-slate-600 transform -translate-x-1/2 z-10"></div>

              {/* Indicateur trophy */}
              <div 
                className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 transition-all duration-1000 ease-out z-20"
                style={{ left: `${percentInit}%` }}
              >
                {percentInit > percentDefie ? (
                  <Trophy size={14} className="text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.8)] animate-bounce" />
                ) : percentInit < percentDefie ? (
                  <Activity size={14} className="text-red-400 drop-shadow-[0_0_8px_rgba(248,113,113,0.8)]" />
                ) : (
                  <div className="w-2 h-2 bg-white rounded-full shadow-lg"></div>
                )}
              </div>
            </div>

            {/* Pourcentages */}
            <div className="flex justify-between mt-1 text-[10px] sm:text-xs font-semibold tabular-nums">
              <span className="text-indigo-400">{percentInit.toFixed(0)}%</span>
              <span className="text-red-400">{percentDefie.toFixed(0)}%</span>
            </div>
          </div>
        </>
      )}
    </div>

    {/* Club Défié */}
    <div className="text-center">
      <div className="relative inline-block mb-3 sm:mb-4">
        <div className="relative">
          <img
            src={war.clubDefie.imageClub || defaultGroupPic}
            alt={war.clubDefie.nom}
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-red-500 shadow-lg shadow-red-500/50 object-cover"
          />
          {/* Effet de brillance */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-white/20 to-transparent animate-pulse"></div>
        </div>
        
      </div>
      <h3 className="font-bold text-base sm:text-xl text-white mb-1 truncate px-2">{war.clubDefie.nom}</h3>
      <p className="text-gray-400 text-xs sm:text-sm flex items-center justify-center gap-1">
        <MapPin size={12} className="sm:w-3.5 sm:h-3.5 flex-shrink-0" />
        <span className="truncate">{war.clubDefie.ville}</span>
      </p>
    </div>
  </div>

  

  {/* Action Buttons for Active War */}
  {war.statut === 'active' && currentUser && isMemberOfParticipatingClub() && (
    <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-slate-800">
      {isParticipating() ? (
        <>
          {currentUser.stravaConnected && (
            <button
              onClick={handleSyncStrava}
              disabled={syncingStrava}
              className="flex-1 sm:flex-initial px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl font-semibold transition disabled:opacity-50 shadow-lg shadow-orange-500/20 text-sm sm:text-base flex items-center justify-center gap-2"
            >
              {syncingStrava ? (
                <>
                  <Activity size={16} className="animate-spin" />
                  <span>Synchronisation...</span>
                </>
              ) : (
                <>
                  <Activity size={16} />
                  <span>Sync Strava</span>
                </>
              )}
            </button>
          )}
          
          <button
            onClick={handleLeaveWar}
            className="flex-1 sm:flex-initial px-4 sm:px-6 py-2.5 sm:py-3 bg-slate-800 hover:bg-slate-700 text-gray-300 rounded-xl font-semibold transition border border-slate-700 hover:border-slate-600 text-sm sm:text-base"
          >
            Quitter
          </button>
        </>
      ) : (
        <button
          onClick={handleJoinWar}
          className="w-full px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl font-semibold transition shadow-lg shadow-green-500/20 text-sm sm:text-base flex items-center justify-center gap-2"
        >
          <Flame size={18} />
          <span>Rejoindre la bataille</span>
        </button>
      )}
    </div>
  )}
</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-8">
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide">
            {[
              { id: 'leaderboard', label: 'Classement', icon: Trophy },
              { id: 'activities', label: 'Activités', icon: Activity },
              { id: 'overview', label: 'Vue d\'ensemble', icon: Target }
              
              
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold whitespace-nowrap transition ${
                    activeTab === tab.id
                      ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/20'
                      : 'bg-slate-800 text-gray-400 hover:text-white hover:bg-slate-700 border border-slate-700'
                  }`}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
            {activeTab === 'overview' && (
  <div className="space-y-6">
    {/* Informations */}
    <div>
      <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <Calendar size={20} className="text-indigo-500" />
        Informations
      </h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="p-4 bg-slate-800 rounded-xl border border-slate-700">
          <div className="text-sm text-gray-400 mb-1">Début</div>
          <div className="font-semibold text-white">
            {new Date(war.dateDebut).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </div>
        </div>
        <div className="p-4 bg-slate-800 rounded-xl border border-slate-700">
          <div className="text-sm text-gray-400 mb-1">Fin</div>
          <div className="font-semibold text-white">
            {new Date(war.dateFin).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </div>
        </div>
        <div className="p-4 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-xl border border-yellow-500/20">
          <div className="text-sm text-gray-400 mb-1">Récompense vainqueur</div>
          <div className="font-bold text-2xl text-yellow-500 flex items-center gap-2">
            <Trophy size={24} />
            {war.pointsEnJeu.vainqueur} pts
          </div>
        </div>
        <div className="p-4 bg-slate-800 rounded-xl border border-slate-700">
          <div className="text-sm text-gray-400 mb-1">Durée</div>
          <div className="font-semibold text-white">{war.dureeJours} jours</div>
        </div>
        
        {/* 👥 Participants actifs */}
        <div className="p-4 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 rounded-xl border border-indigo-500/20 sm:col-span-2">
          <div className="text-sm text-gray-400 mb-2 text-center">Participants actifs</div>
          <div className="flex items-center justify-around gap-4">
            <div className="text-center">
              <div className="font-bold text-2xl text-indigo-400 flex items-center justify-center gap-2">
                <Users size={20} />
                {war.performances.clubInitiateur.participantsActifs}
              </div>
              <div className="text-xs text-gray-500 mt-1">{war.clubInitiateur.nom}</div>
            </div>
            <div className="text-gray-600 text-2xl font-bold">VS</div>
            <div className="text-center">
              <div className="font-bold text-2xl text-purple-400 flex items-center justify-center gap-2">
                <Users size={20} />
                {war.performances.clubDefie.participantsActifs}
              </div>
              <div className="text-xs text-gray-500 mt-1">{war.clubDefie.nom}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Règles */}
    {war.regles && (Object.keys(war.regles).some(key => war.regles[key])) && (
      <div>
        <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Award size={20} className="text-indigo-500" />
          Règles
        </h3>
        <div className="p-4 bg-slate-800 rounded-xl border border-slate-700 space-y-2">
          {war.regles.distanceMin > 0 && (
            <div className="flex items-center gap-2 text-gray-300">
              <div className="w-2 h-2 bg-indigo-500 rounded-full" />
              <span>Distance minimum: <strong>{war.regles.distanceMin} km</strong></span>
            </div>
          )}
          {war.regles.allureMin && (
            <div className="flex items-center gap-2 text-gray-300">
              <div className="w-2 h-2 bg-indigo-500 rounded-full" />
              <span>Allure minimum: <strong>{war.regles.allureMin} min/km</strong></span>
            </div>
          )}
          {war.regles.allureMax && (
            <div className="flex items-center gap-2 text-gray-300">
              <div className="w-2 h-2 bg-indigo-500 rounded-full" />
              <span>Allure maximum: <strong>{war.regles.allureMax} min/km</strong></span>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
)}

            {activeTab === 'activities' && (
              <div>
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Activity size={20} className="text-indigo-500" />
                  Activités récentes
                </h3>
                <div className="space-y-3">
                  {[...war.performances.clubInitiateur.activites, ...war.performances.clubDefie.activites]
                    .sort((a, b) => new Date(b.dateActivite) - new Date(a.dateActivite))
                    .slice(0, 20)
                    .map((activity, index) => (
                      <div key={index} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-4 bg-slate-800 rounded-xl border border-slate-700 hover:border-indigo-500/50 transition">
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                          <img
                            src={activity.user.photoProfil || defaultProfilePic}
                            alt={activity.user.nom}
                            className="w-12 h-12 rounded-full border-2 border-slate-700 shrink-0"
                          />
                          <div className="flex-1 min-w-0 sm:flex-none">
                            <div className="font-semibold text-white truncate">
                              {activity.user.prenom} {activity.user.nom}
                            </div>
                            <div className="text-xs text-gray-500 sm:hidden">
                              {new Date(activity.dateActivite).toLocaleDateString('fr-FR')}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0 w-full sm:w-auto">
                          <div className="text-sm text-gray-400 flex items-center gap-2 sm:gap-3 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Target size={14} />
                              {activity.distance.toFixed(2)} km
                            </span>
                            {activity.allure && (
                              <span className="flex items-center gap-1">
                                <Zap size={14} />
                                {activity.allure.toFixed(2)} min/km
                              </span>
                            )}
                            {activity.denivele > 0 && (
                              <span className="flex items-center gap-1">
                                <TrendingUp size={14} />
                                {activity.denivele}m D+
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="hidden sm:block text-xs text-gray-500 whitespace-nowrap shrink-0">
                          {new Date(activity.dateActivite).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    ))}
                  
                  {[...war.performances.clubInitiateur.activites, ...war.performances.clubDefie.activites].length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <Activity size={48} className="mx-auto mb-4 opacity-50" />
                      <p>Aucune activité pour le moment</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'leaderboard' && (
              <div className="space-y-8">
                {/* Classement Club Initiateur */}
                <div>
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full" />
                    <span className="text-white">{war.clubInitiateur.nom}</span>
                  </h3>
                  <div className="space-y-2">
                    {war.performances.clubInitiateur.participants
                      .sort((a, b) => b.contribution - a.contribution)
                      .map((p, index) => (
                        <div 
                          key={index} 
                          className={`flex items-center gap-3 p-4 rounded-xl border transition ${
                            index === 0 
                              ? 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30' 
                              : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                          }`}
                        >
                          <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold ${
                            index === 0 ? 'bg-yellow-500 text-slate-900' :
                            index === 1 ? 'bg-slate-600 text-white' :
                            index === 2 ? 'bg-orange-700 text-white' :
                            'bg-slate-700 text-gray-400'
                          }`}>
                            {index === 0 ? <Crown size={16} /> : `#${index + 1}`}
                          </div>
                          <img
                            src={p.user.photoProfil || defaultProfilePic}
                            alt={p.user.nom}
                            className="w-10 h-10 rounded-full border-2 border-slate-700"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-white truncate">
                              {p.user.prenom} {p.user.nom}
                            </div>
                            <div className="text-sm text-gray-400">
                              {p.activitesCount} activité{p.activitesCount > 1 ? 's' : ''}
                            </div>
                          </div>
                          <div className="font-bold text-red-500 text-lg">
                            {p.contribution.toFixed(1)} {typeInfo.unite}
                          </div>
                        </div>
                      ))}
                    
                    {war.performances.clubInitiateur.participants.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Users size={40} className="mx-auto mb-3 opacity-50" />
                        <p>Aucun participant</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Classement Club Défié */}
                <div>
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full" />
                    <span className="text-white">{war.clubDefie.nom}</span>
                  </h3>
                  <div className="space-y-2">
                    {war.performances.clubDefie.participants
                      .sort((a, b) => b.contribution - a.contribution)
                      .map((p, index) => (
                        <div 
                          key={index} 
                          className={`flex items-center gap-3 p-4 rounded-xl border transition ${
                            index === 0 
                              ? 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30' 
                              : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                          }`}
                        >
                          <div className={`w-8 h-8 flex items-center justify-center rounded-full font-bold ${
                            index === 0 ? 'bg-yellow-500 text-slate-900' :
                            index === 1 ? 'bg-slate-600 text-white' :
                            index === 2 ? 'bg-orange-700 text-white' :
                            'bg-slate-700 text-gray-400'
                          }`}>
                            {index === 0 ? <Crown size={16} /> : `#${index + 1}`}
                          </div>
                          <img
                            src={p.user.photoProfil || defaultProfilePic}
                            alt={p.user.nom}
                            className="w-10 h-10 rounded-full border-2 border-slate-700"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-white truncate">
                              {p.user.prenom} {p.user.nom}
                            </div>
                            <div className="text-sm text-gray-400">
                              {p.activitesCount} activité{p.activitesCount > 1 ? 's' : ''}
                            </div>
                          </div>
                          <div className="font-bold text-blue-500 text-lg">
                            {p.contribution.toFixed(1)} {typeInfo.unite}
                          </div>
                        </div>
                      ))}
                    
                    {war.performances.clubDefie.participants.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Users size={40} className="mx-auto mb-3 opacity-50" />
                        <p>Aucun participant</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ClubWarPage;